import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import FeatherIcon from "components/FeatherIcon";
import {useDebouncedCallback} from "use-debounce";

interface CompetitionActionCountButtonProps {
    isSelected: boolean;
    disabled?: boolean;
    isFreeSelect: boolean;
    count: number;
    toggleCompetition: () => void;
    setCount: (count: number) => void;
    className?: string;
    maxCount: number;
}

const CompetitionActionCountButton = (props: CompetitionActionCountButtonProps) => {
    const {t} = useTranslation();
    const [inputValue, setInputValue] = useState<string>(props.count.toString());

    useEffect(() => {
        setInputValue(props.count.toString());
    }, [props.count]);

    const debouncedSetCount = useDebouncedCallback((val: number) => {
        props.setCount(val);
    }, 500);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9]/g, "");
        if (value === "") {
            setInputValue("");
            return;
        }

        let parsedValue = parseInt(value, 10);
        if (props.maxCount > 0 && parsedValue > props.maxCount) {
            parsedValue = props.maxCount;
        }

        setInputValue(parsedValue.toString());
        debouncedSetCount(parsedValue);
    };

    if (!props.isSelected) {
        const label = props.isFreeSelect ? t("Select competition") : t("Add");

        return (
            <button
                className={clsx(
                    "CompetitionActionButton min-h-[32px] min-w-[120px] flex flex-row items-center justify-center gap-x-1.5 shrink-0 rounded border border-black bg-white px-2 py-1 text-xs font-bold text-black leading-none hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed",
                    props.disabled && "border-dashed",
                    props.className
                )}
                disabled={props.disabled}
                type={"button"}
                onClick={props.toggleCompetition}
            >
                {!props.isFreeSelect && <FeatherIcon className={"size-5 shrink-0"} name={"plus-circle"} />}
                <span className={"whitespace-nowrap"}>{label}</span>
            </button>
        );
    }

    return (
        <div className={clsx("flex flex-row min-w-[120px] max-w-[120px] items-center justify-between gap-x-1 min-h-[32px]", props.className)}>
            <button
                className="flex size-[32px] items-center justify-center rounded-lg border border-black bg-white hover:bg-gray-50 disabled:opacity-40"
                disabled={props.disabled}
                type="button"
                onClick={() => props.setCount(props.count - 1)}
            >
                <FeatherIcon className="size-[12px]" name="minus" strokeWidth={4} />
            </button>
            <input
                className="text-sm font-bold max-w-[40px] w-auto text-center bg-transparent border-none outline-none focus:ring-0 p-0"
                disabled={props.disabled}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
            />
            <button
                className="flex size-[32px] items-center justify-center rounded-lg border border-black bg-white hover:bg-gray-50 disabled:opacity-40"
                disabled={props.disabled || (props.maxCount > 0 && props.count >= props.maxCount)}
                type="button"
                onClick={() => props.setCount(props.count + 1)}
            >
                <FeatherIcon className="size-[12px]" name="plus" strokeWidth={4} />
            </button>
        </div>
    );
};

export default CompetitionActionCountButton;